import { useRef, useState, useEffect } from 'react';
import { CalendarBlank } from '@phosphor-icons/react';

/**
 * Custom date input that always displays dd.mm.yyyy format
 * Internal value is stored as YYYY-MM-DD for backend compatibility
 */
export function DateInput({ value, onChange, className = '', placeholder = 'dd.mm.yyyy', ...props }) {
  const dateRef = useRef(null);
  const [displayValue, setDisplayValue] = useState('');

  // Convert YYYY-MM-DD to dd.mm.yyyy for display
  useEffect(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-');
      setDisplayValue(`${d}.${m}.${y}`);
    } else if (!value) {
      setDisplayValue('');
    }
  }, [value]);

  // Handle manual text input
  const handleTextChange = (e) => {
    let raw = e.target.value.replace(/[^\d.]/g, '');

    // Auto-add dots after dd and mm
    if (raw.length === 2 && !raw.includes('.')) raw += '.';
    else if (raw.length === 5 && raw.charAt(2) === '.' && !raw.substring(3).includes('.')) raw += '.';

    // Limit length
    if (raw.length > 10) raw = raw.substring(0, 10);

    setDisplayValue(raw);

    // Parse dd.mm.yyyy -> YYYY-MM-DD
    const match = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (match) {
      const isoDate = `${match[3]}-${match[2]}-${match[1]}`;
      onChange({ target: { value: isoDate } });
    } else if (raw === '') {
      onChange({ target: { value: '' } });
    }
  };

  // Handle native date picker change
  const handleNativeDateChange = (e) => {
    onChange({ target: { value: e.target.value } });
  };

  // Open native date picker
  const openCalendar = () => {
    if (dateRef.current) {
      dateRef.current.showPicker?.();
      dateRef.current.click();
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={displayValue}
        onChange={handleTextChange}
        placeholder={placeholder}
        className={`flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
        {...props}
      />
      <button
        type="button"
        onClick={openCalendar}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
        tabIndex={-1}
      >
        <CalendarBlank size={16} />
      </button>
      <input
        ref={dateRef}
        type="date"
        value={value || ''}
        onChange={handleNativeDateChange}
        className="absolute inset-0 opacity-0 pointer-events-none"
        tabIndex={-1}
      />
    </div>
  );
}
